import { AccountCircle, Dialpad, ExpandLess, ExpandMore, MicOff, Pause, PhoneForwarded } from '@mui/icons-material';
import { Box, Collapse, FormHelperText, Button as MuiButton, styled } from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useActiveElement } from '../../hooks/use-active-element';
import { useCallIdentity } from '../../hooks/use-call-identity';
import { useCallParking } from '../../hooks/use-call-parking';
import { useCallSelector, usePhone, usePhoneActions } from '../../hooks/use-phone';
import { usePhoneWidgetProps } from '../../hooks/use-phone-widget-props';
import { RoundButton } from '../round-button';
import { CallActionBox } from './call-styles';
import Keypad from './keypad';
import Transfer from './transfer';

const KeypadBox = styled(Box)({
  width: 264,
  margin: 'auto',
  marginBottom: 24
});

const ButtonBox = styled(Box)({
  margin: '14px 13px 4px',
  display: 'inline-block',
  textAlign: 'center',
  width: '50px',
  '& > button': {
    color: 'white',
    backgroundColor: 'rgb(6, 69, 95)',
    transition: '0.2s',
    width: '48px',
    height: '48px',
    '&:hover': {
      opacity: '0.8',
      transform: 'scale(1.1)',
      transition: '0.2s',
      backgroundColor: 'rgb(6, 69, 95)'
    }
  }
});

const Button = RoundButton;

const ExpandButton = styled(MuiButton)({
  padding: 0
});

const Label = styled(FormHelperText)((styles) => ({
  color: styles.theme.palette.primary.main,
  textAlign: 'center',
  userSelect: 'none',
  '&.Mui-disabled': {
    color: styles.theme.palette.action.disabled
  }
}));

function CallActions(props: { callId: string }) {
  const { activeElement } = useActiveElement();
  const isAudioMuted = useCallSelector(props.callId, (x) => x.isAudioMuted);
  const isCallHeld = useCallSelector(props.callId, (x) => x.onHold);
  const [transferExpanded, setTransferExpanded] = useState(false);
  const [showKeyPad, setShowKeypad] = useState(false);
  const isMounted = useRef(false);
  const { calls, activeCallNumPad, toggleCallCollapse, setActiveCallNumPadExpanded, unsetActiveCallNumPadExpanded } =
    usePhone();
  const { hold, mute, unhold, unmute } = usePhoneActions();
  const collapsed = calls.collapsedCalls[props.callId];
  const remoteIdentity = useCallSelector(props.callId, (x) => x.remoteIdentity || '');
  const { contacts } = useCallIdentity({ phoneNumber: remoteIdentity! });
  const { park } = useCallParking();
  const { widgetProps } = usePhoneWidgetProps();

  const toggleTransferExpanded = useCallback(() => {
    setTransferExpanded((x) => !x);
    setShowKeypad(false);
  }, [setTransferExpanded]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      unsetActiveCallNumPadExpanded();
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      if (transferExpanded || showKeyPad) {
        setActiveCallNumPadExpanded(props.callId);
      } else {
        unsetActiveCallNumPadExpanded();
      }
    }
  }, [transferExpanded, showKeyPad]);

  const handleMute = useCallback(() => {
    if (isAudioMuted) {
      unmute(props.callId);
    } else {
      mute(props.callId);
    }
  }, [isAudioMuted, unmute, props.callId, mute]);

  const handleHold = useCallback(() => {
    if (isCallHeld) {
      unhold(props.callId);
    } else {
      hold(props.callId);
    }
  }, [isCallHeld, unhold, props.callId, hold]);

  const handlePark = useCallback(() => {
    park(props.callId);
  }, [park, props.callId]);

  const toggleShowKeypad = useCallback(() => {
    setShowKeypad((x) => !x);
    setTransferExpanded(false);
  }, [setShowKeypad]);

  const shouldInterceptKeyboard = () => {
    if (activeElement?.nodeName === 'INPUT' || activeCallNumPad.callId !== props.callId) {
      return false;
    }
    return true;
  };

  const showProfile = () => {
    const fireProfile = async () => {
      if (remoteIdentity && widgetProps && widgetProps.events && widgetProps.events.onPatientProfileClick)
        widgetProps.events.onPatientProfileClick(remoteIdentity);
    };
    fireProfile();
  };

  const toggleCollapsed = useCallback(() => {
    toggleCallCollapse(props.callId);
  }, [props.callId, toggleCallCollapse]);

  return (
    <div data-testid="call-action-container-div">
      <Collapse in={!collapsed}>
        <CallActionBox>
          <ButtonBox>
            <Button toggled={isAudioMuted} onClick={handleMute} disabled={isCallHeld}>
              <MicOff />
            </Button>
            <Label disabled={isCallHeld}>Mute</Label>
          </ButtonBox>
          <ButtonBox>
            <Button toggled={isCallHeld} onClick={handleHold}>
              <Pause />
            </Button>
            <Label>Hold</Label>
          </ButtonBox>
          {/* <ButtonBox>
                        <Button onClick={handlePark}>
                            <PhonePaused />
                        </Button>
                        <Label>Park</Label>
                    </ButtonBox> */}
          <ButtonBox>
            <Button disabled={!contacts || !contacts.length} onClick={showProfile}>
              <AccountCircle />
            </Button>
            <Label>Profile</Label>
          </ButtonBox>
          <ButtonBox>
            <Button
              data-testid="call-action-keypad-button"
              toggled={showKeyPad}
              onClick={toggleShowKeypad}
              disabled={isCallHeld}
            >
              <Dialpad />
            </Button>
            <Label disabled={isCallHeld}>Keypad</Label>
          </ButtonBox>
          <ButtonBox>
            <Button
              data-testid="call-action-transfer-button"
              toggled={transferExpanded}
              onClick={toggleTransferExpanded}
              disabled={isCallHeld}
            >
              <PhoneForwarded />
            </Button>
            <Label disabled={isCallHeld}>Transfer</Label>
          </ButtonBox>
        </CallActionBox>
      </Collapse>
      <ExpandButton fullWidth onClick={toggleCollapsed}>
        {collapsed ? <ExpandMore /> : <ExpandLess />}
      </ExpandButton>
      <Collapse in={!collapsed}>
        {transferExpanded && (
          <KeypadBox>
            <Transfer interceptKeyboard={shouldInterceptKeyboard()} callId={props.callId} />
          </KeypadBox>
        )}
        {showKeyPad && (
          <KeypadBox>
            <Keypad sendDTMF={true} callId={props.callId} interceptKeyboard={shouldInterceptKeyboard()} />
          </KeypadBox>
        )}
      </Collapse>
    </div>
  );
}

export default memo(CallActions);
